import styled from '@emotion/styled';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useState } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useLocation } from 'react-router';
import { navigationService, userAuthenticator } from 'src/config/rootService';
import { CountryInput } from 'src/shared/components/CountryInput';
import { Input } from 'src/shared/components/Input';
import * as validator from 'src/shared/helpers/emailValidator';
import { isLastMandatoryTerm } from 'src/shared/helpers/isLastMandatoryTerm';
import { EapAuthLayout } from 'src/shared/layout/EapAuthLayout';
import { EuropeanCountryCodes } from 'src/shared/models/shared';

import { AuthorizationBox } from '../components/AuthorizationBox';
import { ConsentTermsKorea } from './ConsentTermsKorea';
import { ConsentTermsMain } from './ConsentTermsMain';
import { Country, KoreaTerms } from './RegistrationForm';

import { getSortedCountries } from 'src/modules/countries/selectors/countrySelectors';
import logoImg from 'src/public/img/mh-logo-text.svg';
import { ButtonGroup } from 'src/shared/ds/components/ButtonGroup';
import { Input as RRInput, InputType } from 'src/shared/ds/components/Input';
import { color, font } from 'src/shared/styles/variables';
import { DisclaimerModal } from 'src/shared/components/DisclaimerModal';

export enum SingleSignOnFormTestId {
    EmailField = 'SingleSignOnFormTestId.EmailField',
    TermsCheckBox = 'SingleSignOnFormTestId.TermsCheckBox',
    PrivacyCheckBox = 'SingleSignOnFormTestId.PrivacyCheckBox',
    PersonalHealthInfoCheckBox = 'SingleSignOnFormTestId.PersonalHealthInfoCheckBox',
}

const BrandImage = styled.img({
    marginLeft: 32,
    marginTop: 20,
    width: '15em',
    cursor: 'pointer',
    marginRight: 12,
});

const Title = styled.h1(
    {
        color: color.ebonyClay,
        lineHeight: '28px',
        textAlign: 'left',
        paddingBottom: '16px',
        margin: 0,
        width: '100%',
    },
    font.heavy(font.size.extraLarge),
);

const ErrorBanner = styled.div(
    {
        width: '100%',
        color: color.blueBayoux,
        lineHeight: '24px',
        paddingBottom: '8px',
        marginBottom: 0,
        textAlign: 'left',
        backgroundColor: color.lightSteelBlue,
        padding: 10,
        borderRadius: 4,
    },
    font.book(font.size.medium),
);

const FormContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    maxWidth: '500px',
    flexGrow: 1,
});

const Container = styled.div({
    display: 'flex',
    alignItems: 'start',
    paddingTop: '112px',
    justifyContent: 'center',
    flexGrow: 1,
});

const InputContainer = styled.div({
    width: '100%',
    paddingBottom: '40px',
    '& > *': {
        paddingTop: '16px',
    },
});

const CheckboxContainer = styled.div({
    width: '100%',
    paddingTop: '24px',
});

const ButtonsContainer = styled.div({
    width: '100%',
});

interface Response {
    fromPath?: string;
}

const SingleSignOnForm: React.FC<WithTranslation> = ({ t }) => {
    const flags = useFlags();

    const location = useLocation<Response>();
    const isTryingToRegister = location.state?.fromPath == 'revamp-signup' ? true : false;

    const [state, setState] = useState({
        email: '',
        country: 'United States',
        consentToAllAccepted: false,
        termsAccepted: false,
        privacyAccepted: false,
        personalHealthAccepted: false,
        collectAndUseSensitiveInfoAccepted: false,
        provisionOfPersonalInfoUseAccepted: false,
        overseasPersonalInfoXferAccepted: false,
        collectPersonalInfoForMarketingAccepted: false,
        showDisclaimerModal: false,
    });

    const countries = useSelector(getSortedCountries);
    const [errorMessage, setErrorMessage] = useState('');
    const [requestInProgress, setRequestInProgress] = useState(false);

    const {
        email,
        country,
        consentToAllAccepted,
        termsAccepted,
        privacyAccepted,
        personalHealthAccepted,
        collectAndUseSensitiveInfoAccepted,
        provisionOfPersonalInfoUseAccepted,
        overseasPersonalInfoXferAccepted,
        collectPersonalInfoForMarketingAccepted,
    } = state;
    const {
        hideTosPrivacyUpdateMemberWeb,
        showKnTerms,
        showEuPersonalHealthInfoTerm,
        useRegistrationRevamp,
    } = flags;

    const handleEmailChange = (value: string) => {
        setState({
            ...state,
            email: value.trim(),
        });

        if (errorMessage) {
            setErrorMessage('');
        }
    };

    const toggleCheckbox = (checkboxName: string): void => {
        setState({
            ...state,
            [checkboxName]: !state[checkboxName],
        });
    };

    const toggleConsentToAll = (): void => {
        setState({
            ...state,
            consentToAllAccepted: !state.consentToAllAccepted,
            termsAccepted: !state.consentToAllAccepted,
            privacyAccepted: !state.consentToAllAccepted,
            collectAndUseSensitiveInfoAccepted: !state.consentToAllAccepted,
            provisionOfPersonalInfoUseAccepted: !state.consentToAllAccepted,
            overseasPersonalInfoXferAccepted: !state.consentToAllAccepted,
            collectPersonalInfoForMarketingAccepted: !state.consentToAllAccepted,
        });
    };

    const areKnMandatoryTermsAccepted = (): boolean => {
        return (
            state.termsAccepted &&
            state.privacyAccepted &&
            state.collectAndUseSensitiveInfoAccepted &&
            state.provisionOfPersonalInfoUseAccepted &&
            state.overseasPersonalInfoXferAccepted
        );
    };

    const handleAcceptOptionalMarketing = (): void => {
        setState({
            ...state,
            consentToAllAccepted: areKnMandatoryTermsAccepted()
                ? !state.collectPersonalInfoForMarketingAccepted
                : state.consentToAllAccepted,
            collectPersonalInfoForMarketingAccepted: !collectPersonalInfoForMarketingAccepted,
        });
    };

    const handleAutoToggle = (
        hash: Record<string, boolean | string>,
        key: string,
    ): Record<string, boolean> => {
        const {
            consentToAllAccepted: consent,
            collectPersonalInfoForMarketingAccepted: optional,
        } = state;
        const shouldIncludeConsentToAll = !consent && isLastMandatoryTerm(hash, key) && optional;
        const shouldIncludeOptional = consent && isLastMandatoryTerm(hash, key) && !optional;
        return {
            shouldIncludeConsentToAll,
            shouldIncludeOptional,
        };
    };

    const handleAcceptTerms = (hash: Record<string, boolean | string>, key: string): void => {
        const { shouldIncludeConsentToAll, shouldIncludeOptional } = handleAutoToggle(hash, key);
        setState({
            ...state,
            consentToAllAccepted: shouldIncludeConsentToAll
                ? !consentToAllAccepted
                : consentToAllAccepted,
            [key]: !state[key],
            collectPersonalInfoForMarketingAccepted: shouldIncludeOptional
                ? !collectPersonalInfoForMarketingAccepted
                : collectPersonalInfoForMarketingAccepted,
        });
    };

    const updateTermAcceptance = (termName: string): void => {
        switch (termName) {
            case KoreaTerms.CONSENT_TO_ALL_ACCEPTED:
                return toggleConsentToAll();
            case KoreaTerms.OPTIONAL_MARKETING_INFO_USE_ACCEPTED:
                return handleAcceptOptionalMarketing();
            default:
                return handleAcceptTerms(state, termName);
        }
    };

    const handleSubmit = async () => {
        try {
            setRequestInProgress(true);
            // EA China registors must accept disclaimer policies 04/13/21
            if (useRegistrationRevamp && isTryingToRegister && country === Country.CHINA) {
                setState({
                    ...state,
                    showDisclaimerModal: true,
                });
            } else {
                await userAuthenticator.singleSignOnRequest({ email: state.email });
            }
        } catch (error) {
            setErrorMessage(error);
            setRequestInProgress(false);
        }
    };

    const isEuropeanCountry = EuropeanCountryCodes[country] !== undefined;
    const showAdditionalCheckboxesForKorea = country === Country.SOUTH_KOREA && showKnTerms;

    const validateForm = (): boolean => {
        // In the case, the form is only used for login purposes.
        if (!isTryingToRegister) {
            return validator.isEmailValid(email);
        }

        if (validator.isEmailValid(email)) {
            if (isEuropeanCountry) {
                return (
                    termsAccepted &&
                    privacyAccepted &&
                    ((showEuPersonalHealthInfoTerm && personalHealthAccepted) || true)
                );
            } else if (showAdditionalCheckboxesForKorea) {
                return areKnMandatoryTermsAccepted();
            } else {
                return termsAccepted && privacyAccepted;
            }
        }
        return false;
    };

    const handleGoBack = () => {
        navigationService.goBack();
    };

    const oldSSOLoginForm = () => {
        return (
            <EapAuthLayout>
                <AuthorizationBox
                    title={t('auth:single_sign_on_form.single_sign_on')}
                    buttonText={t('auth:single_sign_on_form.send')}
                    additionalInfo={t('auth:single_sign_on_form.additional_information')}
                    isFetching={requestInProgress}
                    onSubmit={handleSubmit}
                    isFormValid={
                        hideTosPrivacyUpdateMemberWeb
                            ? validator.isEmailValid(email)
                            : validateForm()
                    }
                    showBackToLogin
                >
                    <>
                        <Input
                            fieldId="email"
                            label={t('auth:common_form_inputs.labels.email')}
                            type="text"
                            testID={SingleSignOnFormTestId.EmailField}
                            value={email}
                            error={errorMessage}
                            onChange={handleEmailChange}
                        />
                        {!hideTosPrivacyUpdateMemberWeb && (
                            <CountryInput
                                onSelectCountry={(value: string) => {
                                    setState({
                                        ...state,
                                        country: value,
                                    });
                                }}
                                country={state.country}
                            />
                        )}
                        {!showAdditionalCheckboxesForKorea && (
                            <ConsentTermsMain
                                isSSO={true}
                                hideTosPrivacyUpdateMemberWeb={hideTosPrivacyUpdateMemberWeb}
                                showEuPersonalHealthInfoTerm={showEuPersonalHealthInfoTerm}
                                isEuropeanCountry={isEuropeanCountry}
                                onChange={toggleCheckbox}
                                termsAccepted={termsAccepted}
                                privacyAccepted={privacyAccepted}
                                personalHealthAccepted={personalHealthAccepted}
                            />
                        )}
                        {showAdditionalCheckboxesForKorea && (
                            <ConsentTermsKorea
                                onChange={updateTermAcceptance}
                                consentToAllAccepted={consentToAllAccepted}
                                termsAccepted={termsAccepted}
                                privacyAccepted={privacyAccepted}
                                collectAndUseSensitiveInfoAccepted={
                                    collectAndUseSensitiveInfoAccepted
                                }
                                provisionOfPersonalInfoUseAccepted={
                                    provisionOfPersonalInfoUseAccepted
                                }
                                overseasPersonalInfoXferAccepted={overseasPersonalInfoXferAccepted}
                                collectPersonalInfoForMarketingAccepted={
                                    collectPersonalInfoForMarketingAccepted
                                }
                            />
                        )}
                    </>
                </AuthorizationBox>
            </EapAuthLayout>
        );
    };

    const registrationRevampForm = () => {
        const setCountry = value => {
            setState({
                ...state,
                country: value,
            });

            return value.toString();
        };

        const defineEmailErrors = () => {
            if (!email) {
                setErrorMessage(t('auth:registration_form.errors.emailMissing'));
            }

            if (email && !validator.isEmailValid(email)) {
                setErrorMessage(t('auth:registration_form.errors.emailInvalid'));
            }
            return errorMessage;
        };

        return (
            <>
                <BrandImage
                    src={logoImg}
                    onClick={() => {
                        window.location.href = 'https://www.modernhealth.com';
                    }}
                />

                <Container>
                    <FormContainer>
                        <Title>
                            {isTryingToRegister
                                ? t('auth:single_sign_on_form.revamp.register_title')
                                : t('auth:single_sign_on_form.revamp.login_title')}
                        </Title>
                        {errorMessage && <ErrorBanner>{errorMessage}</ErrorBanner>}

                        <InputContainer>
                            <RRInput
                                required
                                fieldId="email"
                                value={email}
                                type={InputType.TextField}
                                label={t('auth:registration_form.labels.email')}
                                onChange={value => handleEmailChange(value.toString())}
                                onBlur={defineEmailErrors}
                            />

                            {isTryingToRegister && (
                                <RRInput
                                    fieldId="country"
                                    type={InputType.CountrySelect}
                                    value={state.country}
                                    options={countries}
                                    label={t('auth:consent_form_page.label.country_of_residence')}
                                    onChange={value => setCountry(value)}
                                />
                            )}
                            {isTryingToRegister && (
                                <CheckboxContainer>
                                    {!showAdditionalCheckboxesForKorea && (
                                        <ConsentTermsMain
                                            isSSO={true}
                                            hideTosPrivacyUpdateMemberWeb={
                                                hideTosPrivacyUpdateMemberWeb
                                            }
                                            showEuPersonalHealthInfoTerm={
                                                showEuPersonalHealthInfoTerm
                                            }
                                            isEuropeanCountry={isEuropeanCountry}
                                            onChange={toggleCheckbox}
                                            termsAccepted={termsAccepted}
                                            privacyAccepted={privacyAccepted}
                                            personalHealthAccepted={personalHealthAccepted}
                                        />
                                    )}

                                    {showAdditionalCheckboxesForKorea && (
                                        <ConsentTermsKorea
                                            onChange={updateTermAcceptance}
                                            consentToAllAccepted={consentToAllAccepted}
                                            termsAccepted={termsAccepted}
                                            privacyAccepted={privacyAccepted}
                                            collectAndUseSensitiveInfoAccepted={
                                                collectAndUseSensitiveInfoAccepted
                                            }
                                            provisionOfPersonalInfoUseAccepted={
                                                provisionOfPersonalInfoUseAccepted
                                            }
                                            overseasPersonalInfoXferAccepted={
                                                overseasPersonalInfoXferAccepted
                                            }
                                            collectPersonalInfoForMarketingAccepted={
                                                collectPersonalInfoForMarketingAccepted
                                            }
                                        />
                                    )}
                                </CheckboxContainer>
                            )}
                        </InputContainer>
                        <ButtonsContainer>
                            <ButtonGroup
                                onFirstButtonClick={handleGoBack}
                                onSecondButtonClick={handleSubmit}
                                secondButtonDisabled={!validateForm()}
                                firstButtonText={t('auth:consent_form_page.back')}
                                secondButtonText={t('auth:consent_form_page.continue')}
                            />
                        </ButtonsContainer>
                    </FormContainer>
                </Container>
                <DisclaimerModal
                    visible={state.showDisclaimerModal}
                    description={t('shared:disclaimer_modal.description', {
                        returnObjects: true,
                    })}
                    onClick={() => userAuthenticator.singleSignOnRequest({ email: state.email })}
                    buttonText={t('shared:disclaimer_modal.buttonText')}
                />
            </>
        );
    };

    return useRegistrationRevamp ? registrationRevampForm() : oldSSOLoginForm();
};

export default withTranslation()(SingleSignOnForm);
